import { defineStore } from 'pinia';
import DocNetMissionService from 'src/services/docnet/DocNetMissionService';
import DocNetApiService from 'src/services/docnet/DocNetApiService';
import { useDocNetIssueStore } from './docnet-issues.store';
import { useDocNetInboxStore } from './docnet-inbox.store';
import { useDocNetSettingsStore } from './docnet-settings.store';
import type {
  DocNetMission,
  DocNetMissionStatus,
  DocNetMissionConversationEntry,
} from 'src/@types/docnet';
import type { MissionProgressEvent } from 'src/types/websocket';
import { mockMissions } from 'src/mocks/docnet/missions';

const missionService = new DocNetMissionService();
const apiService = new DocNetApiService();

export const useDocNetMissionStore = defineStore('docnet_missions', {
  state: () => ({
    missions: [] as DocNetMission[],
    currentMission: null as DocNetMission | null,
    loading: false,
    sending: false,
    // Server-side pagination
    page: 1,
    pageSize: 20,
    totalMissions: 0,
    // Server-side filters — all refetch from the API on change.
    // statusFilter: one of the MissionStatus enum values (e.g. 'active').
    // sourceFilter: one of the frontend source tokens (e.g. 'email', 'manual');
    //   the backend maps these to "[Prefix]" matches against mission name.
    // searchFilter: case-insensitive substring match on name/description.
    statusFilter: '' as string,
    sourceFilter: '' as string,
    searchFilter: '' as string,
  }),

  getters: {
    activeMissions(state): DocNetMission[] {
      return state.missions.filter((m) => m.status === 'active');
    },

    completedMissions(state): DocNetMission[] {
      return state.missions.filter((m) => m.status === 'completed');
    },

    missionSelectOptions(state): { label: string; value: string }[] {
      return state.missions.map((m) => ({
        label: m.name,
        value: m.id,
      }));
    },

    getMissionProgress:
      () =>
      (id: string): number => {
        const taskStore = useDocNetIssueStore();
        const missionTasks = taskStore.issues.filter((t) => t.mission_id === id);

        if (missionTasks.length === 0) {
          return 0;
        }

        const doneTasks = missionTasks.filter(
          (t) => t.status === 'done',
        ).length;
        return Math.round((doneTasks / missionTasks.length) * 100);
      },
  },

  actions: {
    async fetchMissions(silent = false): Promise<void> {
      const settings = useDocNetSettingsStore();
      if (settings.useMockData) {
        this.missions = mockMissions;
        this.totalMissions = mockMissions.length;
        return;
      }
      if (!silent) this.loading = true;
      try {
        const skip = (this.page - 1) * this.pageSize;
        const { items, total } = await missionService.getMissions(
          skip,
          this.pageSize,
          this.statusFilter || undefined,
          this.searchFilter || undefined,
          this.sourceFilter || undefined,
        );
        this.missions = items;
        this.totalMissions = total;
        // Keep currentMission in sync when the list refreshes (status, issues, etc.)
        if (this.currentMission) {
          const updated = items.find((m) => m.id === this.currentMission!.id);
          if (updated) {
            // Merge list data onto current detail, preserving detail-only fields like result
            this.currentMission = {
              ...this.currentMission,
              status: updated.status,
              last_modified_on: updated.last_modified_on,
              issues: updated.issues ?? this.currentMission.issues,
            };
          }
        }
      } catch (error) {
        console.error('[DocNet] Failed to fetch missions:', error);
        throw error;
      } finally {
        if (!silent) this.loading = false;
      }
    },

    async setMissionPage(page: number): Promise<void> {
      this.page = page;
      await this.fetchMissions(true);
    },

    async setStatusFilter(status: string): Promise<void> {
      this.statusFilter = status;
      this.page = 1; // reset to first page when filter changes
      await this.fetchMissions(true);
    },

    async setSourceFilter(source: string): Promise<void> {
      // '' means "all" — sent as no source param to the API.
      this.sourceFilter = source === 'all' ? '' : source;
      this.page = 1;
      await this.fetchMissions(true);
    },

    async setSearchFilter(search: string): Promise<void> {
      // Caller (Vue component) is responsible for debouncing user input.
      const trimmed = search?.trim() ?? '';
      if (trimmed === this.searchFilter) return;
      this.searchFilter = trimmed;
      this.page = 1;
      await this.fetchMissions(true);
    },

    async clearMissionFilters(): Promise<void> {
      this.statusFilter = '';
      this.sourceFilter = '';
      this.searchFilter = '';
      this.page = 1;
      await this.fetchMissions(true);
    },

    /**
     * WS-driven targeted refresh.  Mirrors ``DocNetIssueStore.refreshIssue``
     * — fetches a single mission, syncs it into the cached list and
     * (only if currently viewed) ``currentMission``.  Used by the
     * websocket handler for ``mission_updated`` so a single status
     * flip causes one mission fetch instead of refetching the whole
     * paginated list.
     *
     * Inlined rather than delegating to ``fetchMission`` because
     * ``fetchMission`` unconditionally writes ``currentMission`` —
     * correct for its caller (the detail page navigation handler
     * setting the viewed mission), wrong here (a WS event for
     * mission B must not clobber mission A on the user's open
     * detail page).  Same semantic split that ``refreshIssue`` /
     * ``fetchIssue`` already enforces in the issue store.
     *
     * Errors are swallowed (silent) — WS-driven refreshes shouldn't
     * surface noise to the user; the next list refetch will heal
     * any stale state.
     */
    async refreshMission(id: string): Promise<void> {
      const settings = useDocNetSettingsStore();
      if (settings.useMockData) return;
      try {
        const mission = await missionService.getMissionById(id);
        if (!mission) return;
        const index = this.missions.findIndex((m) => m.id === id);
        if (index !== -1) {
          this.missions[index] = mission;
        }
        if (this.currentMission?.id === id) {
          this.currentMission = mission;
        }
      } catch (error) {
        console.debug('[DocNet] refreshMission failed (silent):', error);
      }
    },

    async fetchMission(id: string, silent = false): Promise<void> {
      const settings = useDocNetSettingsStore();
      if (settings.useMockData) {
        this.currentMission = mockMissions.find(m => m.id === id) ?? null;
        return;
      }
      if (!silent) this.loading = true;
      try {
        const mission = await missionService.getMissionById(id);
        this.currentMission = mission ?? null;

        if (mission) {
          const index = this.missions.findIndex((m) => m.id === id);
          if (index !== -1) {
            this.missions[index] = mission;
          }
        }
      } catch (error) {
        console.error('[DocNet] Failed to fetch mission:', error);
        throw error;
      } finally {
        if (!silent) this.loading = false;
      }
    },

    async createMission(data: { name: string; description?: string; status?: DocNetMissionStatus; owner_id?: string; member_ids?: string[]; due_date?: string }): Promise<DocNetMission> {
      this.loading = true;
      try {
        const mission = await missionService.createMission(data);
        this.missions.push(mission);
        return mission;
      } catch (error) {
        console.error('[DocNet] Failed to create mission:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async updateMission(
      id: string,
      data: Partial<DocNetMission>,
    ): Promise<DocNetMission> {
      this.loading = true;
      try {
        const updated = await missionService.updateMission(id, data);
        const index = this.missions.findIndex((m) => m.id === id);
        if (index !== -1) {
          this.missions[index] = updated;
        }
        if (this.currentMission?.id === id) {
          this.currentMission = updated;
        }
        return updated;
      } catch (error) {
        console.error('[DocNet] Failed to update mission:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async pauseMission(id: string): Promise<DocNetMission> {
      return this.updateMission(id, { status: 'paused' });
    },

    async resumeMission(id: string): Promise<DocNetMission> {
      return this.updateMission(id, { status: 'active' });
    },

    async deleteMission(id: string): Promise<void> {
      await missionService.deleteMission(id);
      this.missions = this.missions.filter((m) => m.id !== id);
      if (this.currentMission?.id === id) {
        this.currentMission = null;
      }
    },

    async restartMission(id: string): Promise<DocNetMission> {
      const updated = await missionService.restartMission(id);
      const idx = this.missions.findIndex((m) => m.id === id);
      if (idx !== -1) this.missions[idx] = updated;
      if (this.currentMission?.id === id) {
        // Fully replace currentMission — don't merge, ensure old result/conversation is cleared
        this.currentMission = { ...updated, result: null };
      }
      // Backend soft-deletes all child issues and pending HITL inbox items
      // on restart — mirror that in the local stores so any view bound to
      // missionIssues/issues/inbox items updates immediately without waiting
      // for a WebSocket refresh.
      const issueStore = useDocNetIssueStore();
      issueStore.missionIssues = issueStore.missionIssues.filter(
        (i) => i.mission_id !== id,
      );
      issueStore.issues = issueStore.issues.filter(
        (i) => i.mission_id !== id,
      );
      const inboxStore = useDocNetInboxStore();
      inboxStore.items = inboxStore.items.filter(
        (item) => item.mission_id !== id || item.status !== 'pending',
      );
      return updated;
    },

    async sendMissionMessage(missionId: string, message: string): Promise<void> {
      this.sending = true;

      // Optimistically add user message to local conversation
      if (this.currentMission?.id === missionId) {
        const result = this.currentMission.result || {};
        const conversation = result.conversation || [];
        // Check if children are still pending — mark message as queued
        const issues = (this.currentMission as any).issues || [];
        const hasPending = issues.some(
          (t: any) =>
            t.assignee_type === 'agent' &&
            !['done', 'cancelled', 'error'].includes(t.status),
        );
        const entry: Record<string, any> = {
          role: 'user',
          text: message,
          timestamp: new Date().toISOString(),
        };
        if (hasPending) {
          entry.queued = true;
        }
        conversation.push(entry);
        this.currentMission.result = { ...result, conversation };
      }

      try {
        const selectedLang = localStorage.getItem('selected_language');
        await apiService.chat({
          prompt: message,
          mission_id: missionId,
          ...(selectedLang ? { language: selectedLang } : {}),
        });
        // Don't re-fetch — websocket events will update the mission reactively.
      } catch (error) {
        console.error('[DocNet] Failed to send mission message:', error);
        throw error;
      } finally {
        this.sending = false;
      }
    },

    async resumeMissionApproval(
      missionId: string,
      action: string,
      resolutionNotes?: string,
    ): Promise<void> {
      this.sending = true;

      // Optimistically update status to active
      if (this.currentMission?.id === missionId) {
        this.currentMission.status = 'active';
        const result = this.currentMission.result || {};
        delete result.waiting_reason;
        delete result.waiting_agent;
        this.currentMission.result = { ...result };
      }

      try {
        await apiService.resumeMission(missionId, action, resolutionNotes);
        // After resume completes, re-fetch mission for latest state
        await this.fetchMission(missionId, true);
      } catch (error) {
        console.error('[DocNet] Failed to resume mission:', error);
        // Revert optimistic update
        await this.fetchMission(missionId, true);
        throw error;
      } finally {
        this.sending = false;
      }
    },

    /**
     * Apply a websocket mission event to the local store state.
     * Called from the mission detail page's websocket subscription to
     * update the UI in real-time without REST re-fetches.
     */
    applyMissionEvent(missionId: string, data: MissionProgressEvent): void {
      // Update currentMission if it matches
      if (this.currentMission?.id === missionId) {
        // Update status
        if (data.status) {
          this.currentMission.status = data.status as DocNetMissionStatus;
        }

        // Update conversation from full array if provided
        if (data.conversation) {
          const result = this.currentMission.result || {};
          result.conversation = data.conversation as DocNetMissionConversationEntry[];
          if (data.response) result.response = data.response;
          if (data.agents_used) result.last_agent = data.agents_used[data.agents_used.length - 1];
          if (data.waiting_reason) result.waiting_reason = data.waiting_reason;
          if (data.waiting_agent) result.waiting_agent = data.waiting_agent;
          this.currentMission.result = { ...result };
        }

        // Update timestamp
        if (data.last_modified_on) {
          this.currentMission.last_modified_on = data.last_modified_on;
        }
      }

      // Also update in the missions list
      const idx = this.missions.findIndex((m) => m.id === missionId);
      if (idx !== -1) {
        if (data.status) {
          this.missions[idx].status = data.status as DocNetMissionStatus;
        }
        if (data.last_modified_on) {
          this.missions[idx].last_modified_on = data.last_modified_on;
        }
      }
    },
  },
});
